<template>
  <v-app id="keep">
    <v-app-bar app clipped-left color="#15489e" v-if="privateRoute()" dark>
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <router-link to="/" class="logo-link">
        <img
          src="@/assets/img/volk-logo-white.png"
          alt="Volk Logo"
          height="30"
          class="ml-3 mr-5 mt-1"
        />
        <span style="opacity:.3" class="caption white--text">v{{ appVersion }}</span>
      </router-link>

      <v-spacer></v-spacer>
      <v-btn icon to="/profile">
        <v-avatar :size="36" class="white lighten-1">
          <v-icon color="primary">person</v-icon>
        </v-avatar>
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      app
      clipped
      v-if="privateRoute() && user"
      width="220"
    >
      <v-list dense>
        <template v-for="(item, i) in navItems">
          <v-layout v-if="item.heading" :key="i" align-center>
            <v-flex xs12>
              <v-subheader v-if="item.heading">{{ item.heading }}</v-subheader>
            </v-flex>
          </v-layout>
          <v-divider
            v-else-if="item.divider"
            :key="i"
            dark
            class="my-2 mt-0"
          ></v-divider>
          <v-list-item v-else :key="i" :to="item.link" :target="item.target">
            <v-list-item-action class="mr-3">
              <v-icon small>{{ item.icon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title class="grey--text">
                {{ item.text
                }}<span v-if="item.new"
                  ><v-chip x-small class="ml-3" color="green" dark
                    >new</v-chip
                  ></span
                >
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- People keep this app open in a tab for days, so the bundle never refreshes
         on its own and they can sit on old code long after a deploy. Forced, with no
         way past it, the same as the employee app. -->
    <v-dialog v-model="updateAvailable" persistent max-width="400">
      <v-card>
        <v-card-text class="text-center pt-6">
          <v-icon size="48" color="primary" class="mb-3">mdi-update</v-icon>
          <div class="text-h6 mb-2">Update required</div>
          <div class="text-body-2">
            A new version of the app is available. Update now to continue.
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn color="primary" depressed block large @click="applyUpdate">
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-main>
      <v-container fluid fill-height class="grey lighten-4">
        <v-layout>
          <v-flex>
            <keep-alive include="DailyReportsList">
              <router-view></router-view>
            </keep-alive>
          </v-flex>
        </v-layout>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters } from "vuex";
import fb from "@/main";
import { APP_VERSION } from "@/version";
export default {
  data: () => ({
    appVersion: APP_VERSION,
    updateAvailable: false,
    latestVersion: null,
    drawer: null,
    navItems: [],
    allNavItems: [
      {
        icon: "build",
        text: "Order Tools",
        link: "/tools/order",
        role: ["Office", "Project Manager", "Yard Manager", "Foreman"],
      },
      {
        icon: "build",
        text: "My Tool Orders",
        link: "/tools/orders",
        role: ["Office", "Project Manager", "Yard Manager", "Foreman"],
      },
      {
        icon: "local_shipping",
        text: "Tool Pickup",
        link: "/tools/pickup-request",
        role: ["Office", "Project Manager", "Yard Manager", "Foreman"],
      },
      {
        icon: "local_shipping",
        text: "My Tool Pickups",
        link: "/tools/pickups",
        role: ["Office", "Project Manager", "Yard Manager", "Foreman"],
      },
      {
        icon: "location_city",
        text: "Tools on Site",
        link: "/tools/tools-on-site",
        role: ["Office", "Project Manager", "Yard Manager", "Foreman"],
      },
      {
        icon: "assignment",
        text: "Work Orders",
        link: "/work-orders/orders",
        role: ["Office", "Project Manager", "Foreman"],
      },
      {
        icon: "delete_sweep",
        text: "My Truck Requests",
        link: "/truck-requests/orders",
        role: ["Office", "Project Manager", "Foreman", "Truck Manager"],
      },
      {
        icon: "today",
        text: "Truck Schedule",
        link: "/truck-requests/schedule",
        role: [
          "Office",
          "Project Manager",
          "Yard Manager",
          "Foreman",
          "Truck Manager",
        ],
      },
      {
        icon: "description",
        text: "Daily Reports",
        link: "/daily-reports",
        role: ["Office", "Project Manager", "Foreman"],
      },
      {
        icon: "watch_later",
        text: "Punch In/Out",
        link: "/punch-dashboard",
        role: [
          "Office",
          "Project Manager",
          "Foreman",
          "Yard Manager",
          "Equipment Manager",
          "Truck Manager",
          "Payroll Clock-In",
        ],
      },
      { divider: true, role: ["Yard Manager"] },
      { heading: "Yard Manager", role: ["Yard Manager"] },
      {
        icon: "receipt",
        text: "Tool Orders",
        link: "/tools/manage/orders",
        role: ["Yard Manager"],
      },
      {
        icon: "access_time",
        text: "Late Orders",
        link: "/tools/manage/late-orders",
        role: ["Yard Manager", "Office"],
      },
      {
        icon: "local_shipping",
        text: "Tool Pickups",
        link: "/tools/manage/pickups",
        role: ["Yard Manager"],
      },
      {
        icon: "build",
        text: "Tool List",
        link: "/tools/manage/list",
        role: ["Yard Manager"],
      },
      {
        icon: "build",
        text: "Tool Rentals",
        link: "/tools/manage/tool-rentals",
        role: ["Yard Manager"],
      },
      {
        divider: true,
        role: ["Truck Manager", "Office"],
      },
      {
        heading: "Truck Manager",
        role: ["Truck Manager", "Office"],
      },
      {
        icon: "delete_sweep",
        text: "Truck Requests",
        link: "/truck-requests/manage/orders",
        role: ["Truck Manager"],
      },
      {
        icon: "access_time",
        text: "Late Orders",
        link: "/truck-requests/manage/late-orders",
        role: ["Truck Manager", "Office"],
      },
      {
        icon: "local_shipping",
        text: "Trucks",
        link: "/truck-requests/manage/trucks",
        role: ["Truck Manager"],
      },
      {
        icon: "inventory",
        text: "Minis",
        link: "/truck-requests/manage/mini-containers",
        role: ["Truck Manager"],
      },
      {
        icon: "drive_eta",
        text: "Driver",
        link: "/truck-requests/manage/driver",
        role: ["Driver"],
      },
      {
        icon: "receipt_long",
        text: "Dump Tickets",
        link: "/truck-requests/manage/dump-tickets/new",
        role: ["Truck Manager", "Driver"],
      },
      {
        icon: "car_repair",
        text: "Service Requests",
        link: "/truck-requests/manage/service",
        role: ["Truck Manager", "Mechanic", "Office", "Equipment Manager"],
      },
      {
        icon: "car_repair",
        text: "Truck Service",
        link: "/truck-requests/service",
        role: [
          "Truck Manager",
          "Driver",
          "Mechanic",
          "Office",
          "Equipment Manager",
        ],
      },
      {
        icon: "insert_chart",
        text: "Dump Reports",
        link: "/truck-requests/dump-report",
        role: ["Office", "Truck Manager"],
      },
      { divider: true, role: ["Office", "Project Manager", "Yard Manager"] },
      {
        heading: "Project Manager",
        role: ["Office", "Project Manager"],
      },
      {
        icon: "assignment",
        text: "Work Orders",
        link: "/work-orders/manage/orders",
        role: ["Office", "Project Manager"],
      },
      {
        divider: true,
        role: ["Office", "Project Manager", "Yard Manager", "Truck Manager"],
      },
      {
        heading: "Office",
        role: ["Office", "Project Manager", "Yard Manager", "Truck Manager"],
      },
      {
        icon: "business",
        text: "Clients",
        link: "/clients",
        role: ["Office"],
      },
      {
        icon: "calendar_month",
        text: "Employee Schedule",
        link: "/employee-schedule",
        role: [
          "Office",
          "Project Manager",
          "Foreman",
          "Yard Manager",
          "Equipment Manager",
          "Truck Manager",
          "Payroll Clock-In",
        ],
      },
      {
        icon: "work",
        text: "Job Sites",
        link: "/jobs",
        role: ["Office", "Project Manager", "Yard Manager", "Truck Manager"],
      },
      {
        icon: "date_range",
        text: "Weekly Summary",
        link: "/weekly-summary",
        role: ["Office", "Project Manager"],
      },
      {
        icon: "assignment_ind",
        text: "Assign Foreman",
        link: "/assign-foreman",
        role: ["Office", "Project Manager"],
      },
      {
        icon: "description",
        text: "Daily Reports",
        link: "/admin/daily-reports",
        role: ["Office", "Project Manager"],
      },
      {
        icon: "description",
        text: "Sign In Sheets",
        link: "/admin/sign-in-sheets",
        role: ["Office", "Project Manager"],
      },
      {
        icon: "location_city",
        text: "DOB Projects",
        link: "/admin/dob-projects",
        role: ["Office", "Project Manager"],
      },
      { divider: true, role: ["admin"] },
      { heading: "Admin", role: ["admin"] },
      { icon: "person", text: "Users", link: "/users", role: ["admin"] },
      {
        icon: "insert_chart",
        text: "Reports",
        link: "/reports",
        role: ["admin", "Truck Manager"],
        permission: "reports",
      },
      {
        divider: true,
        role: ["Office"],
      },
      {
        heading: "Payroll",
        role: ["Office", "Equipment Manager"],
      },
      {
        icon: "person_outline",
        text: "Employees",
        link: "/employees",
        role: ["Office"],
      },
      {
        icon: "payments",
        text: "Payroll",
        link: "/payroll",
        role: ["Office", "Equipment Manager"],
      },
      {
        icon: "mdi-cellphone-text",
        text: "Employee App",
        link: "/employee-app",
        role: ["Office", "Equipment Manager"],
      },
    ],
  }),
  methods: {
    privateRoute() {
      // hideChrome: signed-in pages that show without the app bar and nav
      return this.$route.meta.requiresAuth && !this.$route.meta.hideChrome;
    },
    onVisibilityChange() {
      if (document.visibilityState === "visible") this.checkForUpdate();
    },
    async checkForUpdate() {
      if (this.updateAvailable) return;
      try {
        // version.json is written by scripts/bump-version.mjs and deployed with the
        // bundle, so it always describes the build sitting next to it
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!response.ok) return;

        const data = await response.json();
        if (data.version && data.version !== APP_VERSION) {
          this.latestVersion = data.version;
          this.updateAvailable = true;
        }
      } catch (error) {
        // Offline or a failed fetch - say nothing and try again on the next check
      }
    },
    applyUpdate() {
      // Always a fresh URL rather than a plain reload. A reload only picks up new
      // JavaScript if the browser revalidates index.html, and anyone holding it from
      // before the no-cache header keeps being handed the old html - and its old
      // asset hashes - until that cache entry expires. With no way past this modal,
      // that would strand them on it. main.js strips the marker on the way back in.
      //
      // The version rather than a timestamp: stable for a release, so a second press
      // reuses the same URL instead of filling the cache with one-off entries.
      const url = new URL(window.location.href);
      url.searchParams.set("v", this.latestVersion || String(Date.now()));
      window.location.replace(url.toString());
    },
  },
  computed: {
    ...mapGetters(["user"]),
  },
  created() {
    this.$store.watch(
      (state, getters) => getters.user,
      (newValue) => {
        if (newValue) {
          this.$store.dispatch("getJobSites", fb);
        }
        if (newValue.isAdmin) {
          this.navItems = this.allNavItems;
        } else {
          const permissionGroups = newValue.permissionGroups || [];
          this.navItems = this.allNavItems.filter((i) => {
            return (
              i.role.includes(newValue.role) ||
              !i.role.length ||
              (i.permission && permissionGroups.includes(i.permission))
            );
          });
        }
        if (this.user) {
          fb.analytics.setUserProperties({ role: this.user.role });
        }
      },
    );
  },
  mounted() {
    // On load, and whenever the tab is brought back to the foreground. No polling
    // timer: browsers throttle them in background tabs, and coming back to the tab is
    // the moment that matters anyway.
    this.checkForUpdate();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
};
</script>

<style>
.logo-link {
  text-decoration: none;
}
@media print {
  .v-main {
    padding: 0 !important;
  }
  header.v-sheet.theme--light.v-toolbar.v-app-bar.v-app-bar--clipped.v-app-bar--fixed.secondary {
    display: none !important;
  }
}
</style>
